<template>
  <div class="app-container">
    <el-card shadow="never" class="query-card beauty-card">
      <el-form :model="query" ref="queryForm" :inline="true" v-show="showSearch" class="glass-form">
        <el-form-item label="标签名称" prop="name">
          <el-input
            v-model="query.name"
            placeholder="搜索标签..."
            clearable
            size="small"
            prefix-icon="el-icon-search"
            @keyup.enter.native="handleQuery"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">搜索</el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>

      <div class="table-toolbar">
        <div class="left">
          <el-button
            type="primary"
            icon="el-icon-plus"
            size="mini"
            @click="handleAdd"
          >新增标签</el-button>
        </div>
        <div class="right">
          <el-tooltip class="item" effect="dark" :content="showSearch ? '隐藏搜索' : '显示搜索'" placement="top">
            <el-button size="mini" circle icon="el-icon-search" class="tool-btn" @click="showSearch=!showSearch" />
          </el-tooltip>
          <el-tooltip class="item" effect="dark" content="刷新数据" placement="top">
            <el-button size="mini" circle icon="el-icon-refresh" class="tool-btn" @click="fetchList" />
          </el-tooltip>
        </div>
      </div>

      <el-table
        v-loading="loading"
        :data="tagList"
        class="beauty-table"
        :header-cell-style="{ background: '#f8fafd', color: '#606266', fontWeight: '600' }"
      >
        <el-table-column prop="id" label="ID" width="80" align="center">
          <template slot-scope="{ row }">
            <span class="id-badge">{{ row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="标签名称" min-width="150" align="center">
          <template slot-scope="{ row }">
            <div class="tag-present">
              <span class="custom-tag" :style="getTagStyle(row)">
                {{ row.name }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="color" label="色值配置" width="200">
          <template slot-scope="{ row }">
            <div class="color-info">
              <div class="color-dot" :style="{ backgroundColor: row.color, boxShadow: `0 2px 4px ${row.color}40` }"></div>
              <code class="color-code">{{ row.color }}</code>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="referenceable" label="可引用" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.referenceable === false ? 'danger' : 'success'" size="mini" effect="plain">
              {{ row.referenceable === false ? '禁止' : '允许' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述说明" min-width="200" :show-overflow-tooltip="true">
          <template slot-scope="{ row }">
            <span class="desc-text">{{ row.description || '暂无描述' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdBy" label="创建人" width="130" align="center">
          <template slot-scope="{ row }">
            <span class="time-text">{{ row.createdBy || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建日期" width="160" align="center">
          <template slot-scope="{ row }">
            <span class="time-text">{{ row.createTime }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" fixed="right" width="150">
          <template slot-scope="scope">
            <div class="op-btns">
              <el-button
                size="mini"
                type="text"
                icon="el-icon-edit"
                class="op-edit"
                @click="handleUpdate(scope.row)"
              >编辑</el-button>
              <el-button
                size="mini"
                type="text"
                icon="el-icon-delete"
                class="op-delete"
                @click="handleDelete(scope.row)"
              >删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          v-show="total > 0"
          :current-page="query.pageNo"
          :page-size="query.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 对话框部分 -->
    <el-dialog :title="title" :visible.sync="open" width="450px" append-to-body custom-class="beauty-dialog">
      <el-form ref="form" :model="form" :rules="rules" label-width="80px" label-position="top">
        <el-form-item label="标签名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入标签名称" maxlength="20" show-word-limit />
        </el-form-item>
        <el-form-item label="颜色选择" prop="color">
          <div class="color-picker-wrapper">
            <el-color-picker v-model="form.color" show-alpha :predefine="presetColors" />
            <el-input v-model="form.color" size="small" style="width: 180px; margin-left: 10px" placeholder="#HEX / RGBA" />
          </div>
        </el-form-item>
        <el-form-item label="素材可引用" prop="referenceable">
          <el-switch
            v-model="form.referenceable"
            active-text="允许引用"
            inactive-text="禁止引用"
            :active-value="true"
            :inactive-value="false"
          />
          <div class="ref-tip">关闭后，含此标签的素材将无法新增到文件夹（已有引用不受影响）</div>
        </el-form-item>
        <el-form-item label="备注描述" prop="description">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="添加点描述吧..." />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button class="beauty-btn-outline" @click="cancel">取 消</el-button>
        <el-button type="primary" class="beauty-btn grad-btn" @click="submitForm">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listTags, createTag, updateTag, deleteTag } from '@/api/material/tag';

export default {
  name: "Tags",
  data() {
    return {
      loading: true,
      showSearch: true,
      tagList: [],
      total: 0,
      title: "",
      open: false,
      query: {
        name: undefined,
        pageNo: 1,
        pageSize: 10
      },
      form: {},
      rules: {
        name: [{ required: true, message: "标题怎么能空呢？", trigger: "blur" }],
        color: [{ required: true, message: "选个好看的颜色吧", trigger: "change" }]
      },
      presetColors: [
        '#ff4d4f', '#ff7a45', '#ffa940', '#ffc53d', '#ffec3d',
        '#bae637', '#73d13d', '#36cfc9', '#40a9ff', '#597ef7',
        '#9254de', '#f759ab', '#8c8c8c', '#262626'
      ]
    };
  },
  created() {
    this.fetchList();
  },
  methods: {
    async fetchList() {
      this.loading = true;
      try {
        const response = await listTags(this.query);
        this.tagList = response.data.records || response.data || [];
        this.total = response.data.total || this.tagList.length;
      } finally {
        this.loading = false;
      }
    },
    getTagStyle(row) {
      const color = row.color || '#409EFF';
      const contrast = this.getContrastColor(color);
      return {
        backgroundColor: color,
        color: contrast,
        boxShadow: `0 4px 10px ${color}40`,
        border: 'none'
      };
    },
    cancel() {
      this.open = false;
      this.reset();
    },
    reset() {
      this.form = {
        id: undefined,
        name: undefined,
        color: '#409EFF',
        referenceable: true,
        description: undefined
      };
    },
    handleQuery() {
      this.query.pageNo = 1;
      this.fetchList();
    },
    resetQuery() {
      this.query = {
        name: undefined,
        pageNo: 1,
        pageSize: 10
      };
      this.handleQuery();
    },
    handleSizeChange(val) {
      this.query.pageSize = val;
      this.fetchList();
    },
    handleCurrentChange(val) {
      this.query.pageNo = val;
      this.fetchList();
    },
    handleAdd() {
      this.reset();
      this.open = true;
      this.title = "✨ 新增材质标签";
    },
    handleUpdate(row) {
      this.reset();
      this.form = { ...row };
      this.open = true;
      this.title = "📝 修改标签";
    },
    submitForm() {
      this.$refs["form"].validate(async valid => {
        if (valid) {
          try {
            if (this.form.id != undefined) {
              await updateTag(this.form.id, this.form);
              this.$message.success("修改成功了哦");
            } else {
              await createTag(this.form);
              this.$message.success("太棒了，新增成功");
            }
            this.open = false;
            this.fetchList();
          } catch (e) {
            // Error is handled by interceptor, but we catch to stop execution
          }
        }
      });
    },
    handleDelete(row) {
      this.$confirm(`确定要挥别标签 "${row.name}" 吗？`, "重要提醒", {
        confirmButtonText: "狠心删除",
        cancelButtonText: "留它一命",
        type: "warning",
        customClass: 'beauty-message-box'
      }).then(async () => {
        try {
          await deleteTag(row.id);
          this.fetchList();
          this.$message.success("清理干净啦");
        } catch (e) {
          // If deletion is blocked (e.g. by associations), the error message will be shown by axios interceptor
        }
      }).catch(() => {});
    },
    getContrastColor(colorStr) {
      if (!colorStr) return '#fff';
      
      let r, g, b;
      if (colorStr.startsWith('#')) {
        const hex = colorStr.replace('#', '');
        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
      } else if (colorStr.startsWith('rgb')) {
        const parts = colorStr.match(/\d+/g);
        r = parseInt(parts[0]);
        g = parseInt(parts[1]);
        b = parseInt(parts[2]);
      } else {
        return '#fff';
      }
      
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.6 ? '#1a1a1a' : '#ffffff';
    }
  }
};
</script>

<style scoped>
.app-container {
  padding: 0;
}

/* Tool Buttons */

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Tool Buttons */
.tool-btn {
  font-size: 16px;
  padding: 8px;
  margin-left: 8px;
  color: #606266;
  border-color: transparent;
  background: transparent;
}

.tool-btn:hover {
  background: #ecf5ff;
  color: #409EFF;
}

.id-badge {
  color: #909399;
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
}

.tag-present {
  display: flex;
  justify-content: center;
}

.custom-tag {
  padding: 4px 12px;
  border-radius: 4px; /* Standardize with global look */
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
  line-height: normal;
}

.color-info {
  display: flex;
  align-items: center;
}

.color-dot {
  width: 16px;
  height: 16px;
  border-radius: 4px; /* Semi-rounded for modern look */
  margin-right: 12px;
  border: 1px solid rgba(0,0,0,0.1);
}

.color-code {
  font-family: 'JetBrains Mono', Consolas, monospace;
  font-size: 13px;
  color: #606266;
}

.op-btns {
  display: flex;
  justify-content: center;
}

.pagination-container {
  margin-top: 25px;
  display: flex;
  justify-content: flex-end;
}

/* Dialog internal styling */
.color-picker-wrapper {
  display: flex;
  align-items: center;
  background: #f5f7fa;
  padding: 10px;
  border-radius: 6px;
}

.ref-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}
</style>
